import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { Table, Modal, Typography, Breadcrumb, List } from "antd";

import moment from "moment-timezone";
// import { Button } from "../../components/ui/button";
import { format } from "date-fns";
import { CalendarIcon, CheckIcon } from "@heroicons/react/16/solid";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { ChevronsUpDown, Download, RefreshCw } from "lucide-react";
import {
  get_call_logs,
  list_all_languages,
  get_data_to_download,
} from "../../api/InterpreterApi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { GlobalContext } from "../../App";

const DragIndexContext = createContext({ active: -1, over: -1 });
const dragActiveStyle = (dragState, id) => {
  const { active, over, direction } = dragState;
  // drag active style
  let style = {};
  if (active && active === id) {
    style = { backgroundColor: "gray", opacity: 0.5 };
  }
  // dragover dashed style
  else if (over && id === over && active !== over) {
    style =
      direction === "right"
        ? { borderRight: "1px dashed gray" }
        : { borderLeft: "1px dashed gray" };
  }
  return style;
};
const TableBodyCell = (props) => {
  const dragState = useContext(DragIndexContext);
  return (
    <td
      {...props}
      style={Object.assign(
        Object.assign({}, props.style),
        dragActiveStyle(dragState, props.id)
      )}
    />
  );
};

const TableHeaderCell = (props) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style = Object.assign(
    Object.assign(
      Object.assign(Object.assign({}, props.style), { cursor: "move" }),
      isDragging
        ? { position: "relative", zIndex: 9999, userSelect: "none" }
        : {}
    ),
    dragActiveStyle(dragState, props.id)
  );
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const CallsReport = () => {
  const { setToastMessage, setToastSeverity, setToastOpen } =
    useContext(GlobalContext);
  const [date, setDate] = useState({
    from: moment
      .tz(new Date(), "America/New_York")
      .format("YYYY-MM-DD HH:mm:ss"),
    to: moment.tz(new Date(), "America/New_York").format("YYYY-MM-DD HH:mm:ss"),
  });
  const [isDownloading, setIsDownloading] = useState(false);
  var _a;
  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
  const [rowCount, setRowCount] = useState(20);
  const [openSections, setOpenSections] = useState({
    customer: false,
    interpreter: false,
    language: false,
  });
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedInterpreters, setSelectedInterpreters] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  // State for call logs data
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [timezone, setTimezone] = useState("America/New_York"); // Default to EST
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
  });

  // Function to manually refresh data
  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchCallLogs();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 500); // Show refresh animation for at least 500ms for better UX
    }
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    const today = moment
      .tz(moment().format("YYYY-MM-DD"), timezone)
      .startOf("day")
      .toDate();
    const todayEnd = moment
      .tz(moment().format("YYYY-MM-DD"), timezone)
      .endOf("day")
      .toDate();
    setDate({
      from: today,
      to: todayEnd,
    });
    setSelectedCustomers([]);
    setSelectedInterpreters([]);
    setSelectedLanguages([]);
  };

  // Function to fetch call logs - memoized to avoid stale closures in polling
  const fetchCallLogs = useCallback(async () => {
    setApiLoading(true);
    try {
      // Prepare filters
      const filters = {};

      if (date?.from) {
        filters.start_date = moment
          .tz(moment(date.from).format("YYYY-MM-DD"), timezone)
          .startOf("day")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      if (date?.to) {
        filters.end_date = moment
          .tz(moment(date.to).format("YYYY-MM-DD"), timezone)
          .add(1, "day") // move to next day
          .startOf("day") // 00:00 in EST
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      // Add other filters if selected
      if (selectedCustomers.length > 0) {
        filters.customers = selectedCustomers.join(",");
      }
      if (selectedInterpreters.length > 0) {
        filters.interpreters = selectedInterpreters.join(",");
      }
      if (selectedLanguages.length > 0) {
        filters.languages = selectedLanguages.join(",");
      }
      filters.page = pagination.current;
      filters.page_size = pagination.pageSize;
      setLoading(true);
      const response = await get_call_logs(filters);
      setCallLogs(response.data.items || []);

      // Set the total count for frontend pagination
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching call logs:", error);
    } finally {
      setLoading(false);
      setApiLoading(false);
    }
  }, [
    date,
    selectedCustomers,
    selectedInterpreters,
    selectedLanguages,
    pagination.current,
    pagination.pageSize,
  ]);

  // Setup polling - always use the latest filters via memoized fetchCallLogs
  useEffect(() => {
    if (apiLoading) return;
    const pollingInterval = setInterval(() => {
      fetchCallLogs();
    }, 60000 * 15); // 15 minutes
    return () => clearInterval(pollingInterval);
  }, [fetchCallLogs, apiLoading]);

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState({ title: "", content: "" });

  const [attributesModalVisible, setAttributesModalVisible] = useState(false);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesList, setAttributesList] = useState([]);
  const [currentEngagementId, setCurrentEngagementId] = useState(null);
  const [languages, setLanguages] = useState([]);
  const getData = async () => {
    let response = await list_all_languages();
    setLanguages(response.data);
  };

  // Define columns with access to the showRemarks function
  const baseColumns = [
    {
      title: "Start Time",
      dataIndex: "engagement_start_ts",
      width: 100,
      fixed: "left",
      render: (text) => formatDate(text, timezone),
    },
    { title: "Customer Name", dataIndex: "customer_name", width: 170 },

    { title: "Language", dataIndex: "language", width: 100 },

    {
      title: "Duration",
      dataIndex: "interpretation_duration_s",
      width: 60,
      render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
    },
    {
      title: "Rate",
      dataIndex: "interpreter_bill_rate",
      width: 60,
      render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
    },
    {
      title: "Payout",
      dataIndex: "interpreter_bill",
      width: 60,
      render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
    },
    { title: "Channel", dataIndex: "channel", width: 70 },
  ];

  const [columns, setColumns] = useState(() =>
    baseColumns.map((column, i) => ({
      ...column,
      key: `${i}`,
      onHeaderCell: () => ({ id: `${i}` }),
      onCell: () => ({ id: `${i}` }),
    }))
  );

  useEffect(() => {
    setColumns(
      baseColumns.map((column, i) => ({
        ...column,
        key: `${i}`,
        onHeaderCell: () => ({ id: `${i}` }),
        onCell: () => ({ id: `${i}` }),
      }))
    );
  }, [timezone]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== (over === null || over === void 0 ? void 0 : over.id)) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex(
          (i) =>
            i.key ===
            (active === null || active === void 0 ? void 0 : active.id)
        );
        const overIndex = prevState.findIndex(
          (i) => i.key === (over === null || over === void 0 ? void 0 : over.id)
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = columns.findIndex((i) => i.key === active.id);
    const overIndex = columns.findIndex(
      (i) => i.key === (over === null || over === void 0 ? void 0 : over.id)
    );
    setDragIndex({
      active: active.id,
      over: over === null || over === void 0 ? void 0 : over.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  // Function to convert data to CSV and download it
  const downloadCSV = async () => {
    setIsDownloading(true);
    // setDownloadProgress(0);

    try {
      // Prepare filters
      const filters = {};

      // Add date range if available
      if (date?.from) {
        filters.start_date = format(date.from, "yyyy-MM-dd");
      }
      if (date?.to) {
        filters.end_date = format(date.to, "yyyy-MM-dd");
      }

      // Add other filters if selected
      if (selectedCustomers.length > 0) {
        filters.customers = selectedCustomers.join(",");
      }
      if (selectedInterpreters.length > 0) {
        filters.interpreters = selectedInterpreters.join(",");
      }
      if (selectedLanguages.length > 0) {
        filters.languages = selectedLanguages.join(",");
      }
      // Use chunked download with progress callback
      let response = await get_data_to_download(filters, (count) => {
        setDownloadProgress(count);
      });
      // Get visible columns (in the current order)
      const visibleColumns = columns;

      // Create CSV header row
      const header = visibleColumns.map((col) => `"${col.title}"`).join(",");

      // Format dates in the CSV data
      const formattedData = response.data.map((item) => ({
        ...item,
        engagement_start_ts: formatDateForCSV(
          item.engagement_start_ts,
          timezone
        ),
        engagement_end_ts: formatDateForCSV(item.engagement_end_ts, timezone),
        // Add other date fields here if needed
      }));

      // Create CSV data rows
      const csvRows = formattedData.map((row) => {
        return visibleColumns
          .map((column) => {
            // Handle special cases for columns with render functions
            const value = row[column.dataIndex];
            // Escape quotes and wrap in quotes to handle commas in content
            return `"${value ? String(value).replace(/"/g, '""') : ""}"`;
          })
          .join(",");
      });

      // Combine header and rows
      const csvContent = [header, ...csvRows].join("\n");

      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a download link and trigger the download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `calls_report_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      setToastMessage("Error downloading CSV. Please try again.");
      setToastOpen(true);
      setToastSeverity("error");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Function to format date based on selected timezone
  const formatDate = (dateString, timezone) => {
    if (!dateString) return "-";
    return moment.utc(dateString).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
  };

  // Function to format date for CSV download (always use UTC)
  const formatDateForCSV = (dateString, timezone) => {
    if (!dateString) return "-";
    return moment.utc(dateString).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
  };

  return (
    <div>
      <Breadcrumb
        className="!mb-6"
        items={[
          {
            title: "Home",
          },
          {
            title: "Interpreter Call Logs",
          },
        ]}
      />
      <div className="grid lg:grid-cols-2 xl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-10 mb-10">
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Date Range{" "}
            <span className="text-xs text-gray-400">(max 31 days)</span>
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "justify-start text-left font-normal cursor-pointer ",
                  !date && "text-muted-foreground"
                )}
                onClick={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    date: !prev.date,
                  }))
                }
              >
                <CalendarIcon />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} -{" "}
                      {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-1 z-50 bg-white" align="center">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                disabled={(calendarDate) => {
                  // If we have a selected start date, disable dates that would make range > 31 days
                  if (
                    date?.from &&
                    !date?.to &&
                    moment(date.from).isValid() &&
                    moment(calendarDate).isValid()
                  ) {
                    const daysDifference = Math.abs(
                      moment(calendarDate).diff(moment(date.from), "days")
                    );
                    return daysDifference > 31;
                  }
                  return false;
                }}
                // onSelect={setDate}
                onSelect={(newDate) => {
                  // Handle case where newDate is undefined/null - default to today
                  if (!newDate) {
                    setDate(getTodayDateRange());
                    return;
                  }

                  // Handle case where from date is cleared - default to today
                  if (!newDate.from) {
                    setDate(getTodayDateRange());
                    return;
                  }

                  // Check if range is more than 31 days (only if both dates exist)
                  if (newDate.to && newDate.from) {
                    const daysDifference = moment(newDate.to).diff(
                      moment(newDate.from),
                      "days"
                    );
                    if (daysDifference > 31) {
                      // Show error message
                      setToastMessage(
                        "Date range cannot exceed 31 days. Please select a smaller range."
                      );
                      setToastOpen(true);
                      setToastSeverity("error");
                      return;
                    }
                  }

                  // Start of day in the selected timezone
                  const fromDate = moment
                    .tz(moment(newDate.from).format("YYYY-MM-DD"), timezone)
                    .toDate();

                  let toDate;
                  if (newDate.to && moment(newDate.to).isValid()) {
                    // End of day in the selected timezone for range
                    toDate = moment
                      .tz(moment(newDate.to).format("YYYY-MM-DD"), timezone)
                      .startOf("day")
                      .toDate();
                  } else {
                    // For single day selection or when to date is invalid, set to end of the same day
                    toDate = moment
                      .tz(moment(newDate.from).format("YYYY-MM-DD"), timezone)
                      .endOf("day")
                      .toDate();
                  }

                  setDate({
                    from: fromDate,
                    to: toDate,
                  });
                }}
                numberOfMonths={1}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Language Dropdown */}
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Languages
          </label>
          <Popover
            open={openSections.language}
            onOpenChange={(val) =>
              setOpenSections((prev) => ({ ...prev, language: val }))
            }
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openSections.language}
                className="justify-between cursor-pointer"
              >
                <span className="line-clamp-1">
                  {selectedLanguages.length
                    ? `${selectedLanguages.length} selected`
                    : "Select language..."}
                </span>
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput placeholder="Search language..." />
                <CommandList>
                  <CommandEmpty>No Languages found.</CommandEmpty>
                  <CommandGroup>
                    {languages.map((lang) => (
                      <CommandItem
                        key={lang.name}
                        value={lang.name}
                        className={"cursor-pointer"}
                        onSelect={() => {
                          setSelectedLanguages((prev) =>
                            prev.includes(lang.name)
                              ? prev.filter((v) => v !== lang.name)
                              : [...prev, lang.name]
                          );
                        }}
                      >
                        {lang.name}
                        <CheckIcon
                          className={`ml-auto ${
                            selectedLanguages.includes(lang.name)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="mt-1 gap-1 flex flex-row flex-wrap max-h-20 overflow-y-auto items-start justify-start content-start">
            {selectedLanguages.map((langCode, idx) => {
              const language = languages.find((l) => l.code === langCode);
              const langName = language ? language.name : langCode;
              return (
                <span
                  key={langCode}
                  className="inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                >
                  {langName}
                  <button
                    type="button"
                    className="group relative -mr-1 size-3.5 rounded-xs hover:bg-gray-500/20"
                    onClick={() =>
                      setSelectedLanguages((prev) =>
                        prev.filter((lang) => lang !== langCode)
                      )
                    }
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                    <span className="absolute -inset-1" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Interpreter Report</h1>
        <div className="flex space-x-2 items-end">
          <div className="flex flex-col space-x-2">
            <span className="text-sm text-gray-600">Timezone:</span>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">
                  Eastern Time (EST)
                </SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer border-red-500 text-red-500 hover:bg-red-50"
            onClick={clearAllFilters}
          >
            Clear Filters
          </Button>
          {/* <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={fetchCallLogs}
          >
            Apply Filters
          </Button> */}
          <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={downloadCSV}
            disabled={isDownloading}
          >
            <Download className="h-4 w-4" />
            {isDownloading ? "Downloading..." : "Download CSV"}
          </Button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={columns.map((i) => i.key)}
          strategy={horizontalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            <Table
              rowHoverable={false}
              size="small"
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
              bordered
              rowKey="engagement_id"
              columns={columns}
              dataSource={callLogs}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `${total} records`,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
                size: "default",
                position: ["bottomRight"],
                defaultPageSize: 30,
                current: pagination.current,
                pageSize: pagination.pageSize,
                onChange: (page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: 1, // Reset to first page when changing page size
                    pageSize: size,
                  }));
                },
              }}
              components={{
                header: { cell: TableHeaderCell },
                body: { cell: TableBodyCell },
              }}
            />
          </DragIndexContext.Provider>
        </SortableContext>
        <DragOverlay>
          <th style={{ backgroundColor: "gray", padding: 16 }}>
            {(_a =
              columns[columns.findIndex((i) => i.key === dragIndex.active)]) ===
              null || _a === void 0
              ? void 0
              : _a.title}
          </th>
        </DragOverlay>

        {/* Modal for displaying remarks */}
        <Modal
          title={modalContent.title}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <Typography.Paragraph>{modalContent.content}</Typography.Paragraph>
        </Modal>

        {/* Modal for displaying attributes */}
        <Modal
          title={`Attributes for Engagement ID: ${currentEngagementId}`}
          open={attributesModalVisible}
          onCancel={() => setAttributesModalVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setAttributesModalVisible(false)}
            >
              Close
            </Button>,
          ]}
        >
          {attributesLoading ? (
            <div className="flex justify-center items-center py-4">
              Loading attributes...
            </div>
          ) : attributesList.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={attributesList}
              renderItem={(item) => (
                <List.Item>
                  <Typography.Text strong>
                    {item.attribute_name}:
                  </Typography.Text>{" "}
                  {item.attribute_value}
                </List.Item>
              )}
            />
          ) : (
            <Typography.Paragraph>
              No attributes found for this engagement.
            </Typography.Paragraph>
          )}
        </Modal>
      </DndContext>
    </div>
  );
};
export default CallsReport;
